/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useCallback, useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { PROJECT_TITLE } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { ProjectIcon } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import { ProjectActionMenu } from '@atlassian/jira-navigation-project-action-menu/src/ui/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { ProjectHeader_inner_project$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_inner_project.graphql';
import type { ProjectHeader_query$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_query.graphql';
import type { ProjectHeader_project$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_project.graphql';
import type { ProjectHeader_inner_query$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_inner_query.graphql';
import BaseHeader, {
	type WithInlineEditProps,
} from '@atlassian/jira-horizontal-nav-header/src/common/ui/BaseHeader.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import isWithinLast7Days from '@atlassian/jira-onboarding/src/utils/isWithinLast7Days.tsx';
import { NavigationInviteButton } from '../invite-button/NavigationInviteButton.tsx';
import { ExtraComponentsFar } from '../extra-components-far/ExtraComponentsFar.tsx';
import { useRenameProject } from '../../../../services/rename-project/useRenameProject.tsx';
import { HorizontalNavProjectBreadcrumbs } from '../breadcrumbs/HorizontalNavProjectBreadcrumbs.tsx';
import { NavigationTeamButton } from '../team-button/NavigationTeamButton.tsx';
import ProjectAvatar from './project-avatar/ProjectAvatar.tsx';

export const ProjectHeader = (props: {
	queryFragment: ProjectHeader_query$key;
	projectFragment: ProjectHeader_project$key;
}) => {
	const { queryFragment, projectFragment } = props;
	const data = useFragment(
		graphql`
			fragment ProjectHeader_query on Query {
				...ProjectHeader_inner_query
			}
		`,
		queryFragment,
	);

	const project = useFragment(
		graphql`
			fragment ProjectHeader_project on JiraProject {
				...ProjectHeader_inner_project
			}
		`,
		projectFragment,
	);

	const analyticsData = useMemo(
		() => ({
			navigationContainer: PROJECT_TITLE,
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={analyticsData}>
			<ProjectHeaderInner {...props} queryFragment={data} projectFragment={project} />
		</ContextualAnalyticsData>
	);
};

const ProjectHeaderInner = ({
	queryFragment,
	projectFragment,
}: {
	queryFragment: ProjectHeader_inner_query$key;
	projectFragment: ProjectHeader_inner_project$key;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isFullscreen] = useIsFullscreen();
	const { updateProjectName, validateProjectName } = useRenameProject();

	const data = useFragment(
		graphql`
			fragment ProjectHeader_inner_query on Query {
				jira @required(action: THROW) {
					jwmLicensing(cloudId: $cloudId) {
						currentUserSeatEdition
					}
				}
			}
		`,
		queryFragment,
	);

	const project = useFragment(
		graphql`
			fragment ProjectHeader_inner_project on JiraProject
			@argumentDefinitions(
				showTeamButton: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-team-button.relayprovider"
				}
				showInviteButton: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-invite-button.relayprovider"
				}
			) {
				...ProjectAvatar
				...NavigationTeamButton_project @include(if: $showTeamButton)
				...NavigationInviteButton_project @include(if: $showInviteButton)
				name
				key @required(action: THROW)
				projectType @required(action: THROW)
				projectId
				avatar {
					large
				}
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
				created @include(if: $showInviteButton)
				...ExtraComponentsFar
				...HorizontalNavProjectBreadcrumbs
			}
		`,
		projectFragment,
	);

	const isBusinessProject = project.projectType === 'BUSINESS';
	const withInlineEdit = isBusinessProject;
	const isCorePremiumUserSeat = data.jira?.jwmLicensing?.currentUserSeatEdition === 'PREMIUM';
	const canEditProjectConfig = project?.canEditProjectConfig?.canPerform ?? false;

	const icon = useMemo(() => {
		const avatar = (
			<Box xcss={iconStyles}>
				<ProjectAvatar projectRef={project} />
			</Box>
		);

		if (withInlineEdit) {
			return avatar;
		}

		if (!project?.avatar?.large) {
			return null;
		}

		return (
			<ProjectIcon
				data-testid="horizontal-nav-header.ui.project-header.project-icon"
				data-vc="horizontal-nav-project-icon"
				src={project.avatar.large}
				alt=""
			/>
		);
	}, [project, withInlineEdit]);

	const handleProjectActionDropDownClick = useCallback(
		() => fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'projectActionsDropdown', {}),
		[createAnalyticsEvent],
	);

	const onUpdateProjectName = useCallback(
		(name: string) => updateProjectName(Number(project?.projectId), name),
		[project?.projectId, updateProjectName],
	);

	let headerProps = {};
	if (withInlineEdit && project?.projectId) {
		const inlineEditProps: WithInlineEditProps = {
			withInlineEdit: true,
			onConfirm: onUpdateProjectName,
			onValidate: validateProjectName,
			id: Number(project.projectId),
		};

		headerProps = canEditProjectConfig ? inlineEditProps : {};
	}

	const now = new Date();

	const showInviteButton =
		project?.created &&
		canEditProjectConfig &&
		project.projectId === '10000' && // only setting invite button for the default project
		isWithinLast7Days(project.created, now) &&
		expVal('jfind-restore-invite-button-experiment', 'enrolled', false);

	const extraComponentsClose = useMemo((): ReactNode => {
		if (!project) {
			return null;
		}

		return (
			<>
				{showInviteButton && <NavigationInviteButton projectFragment={project} scope="project" />}
				{!showInviteButton && fg('team-connection-visibility-in-jira') && (
					<NavigationTeamButton projectFragment={project} scope="project" />
				)}
				<ProjectActionMenu
					data-testid="horizontal-nav-header.ui.project-header.project-action-menu"
					key="ProjectActions"
					projectKey={project.key}
					isCorePremiumUserSeat={isCorePremiumUserSeat}
					withThemedTrigger
					onClick={handleProjectActionDropDownClick}
					shouldRenderToParent
				/>
			</>
		);
	}, [project, showInviteButton, isCorePremiumUserSeat, handleProjectActionDropDownClick]);

	if (!project) {
		return null;
	}

	return (
		<BaseHeader
			title={project.name}
			{...headerProps}
			icon={icon}
			isLoading={project == null}
			extraComponentsClose={extraComponentsClose}
			testId="horizontal-nav-header.ui.project-header.header"
			extraComponentsFar={<ExtraComponentsFar projectFragment={project} />}
			extraComponentsAbove={
				!isFullscreen && <HorizontalNavProjectBreadcrumbs projectFragment={project} />
			}
		/>
	);
};

const iconStyles = xcss({
	width: '20px',
	height: '20px',
	boxSizing: 'border-box',
});
